import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/clientWeb/components/ui/dropdown-menu";
import { Switch } from "@/clientWeb/components/ui/switch";
import { useTranslation } from "@/clientWeb/i18n/LanguageContext";
import { useMeetingPreferencesStore } from "@/features/meeting/store/meeting-preferences.store";
import type { PreferredLayout } from "@/features/meeting/store/meeting-preferences.store";
import {
  useMeetingLayoutStore,
  AUTO_VISIBLE_VIDEOS,
  tilesForVisibleVideos,
  visibleVideosForTiles,
} from "@/features/meeting/store/meeting-layout.store";
import { useDeviceEnumeration, useDevicesStore, VIDEO_QUALITY_PRESETS } from "@/features/devices";
import type { VideoQuality } from "@/features/devices";

export function VideoSettings() {
  const t = useTranslation();
  const [advancedOpen, setAdvancedOpen] = useState(true);
  // The real preference, shared with the lobby and the meeting grid. This was
  // local state defaulting to true while nothing read it — the switch showed
  // "on" and mirrored nothing.
  const mirrorView = useMeetingPreferencesStore((s) => s.mirrorOwnVideo);
  const setMirrorView = useMeetingPreferencesStore((s) => s.setMirrorOwnVideo);
  // Real cameras, same store as the lobby and the control bar. This was the
  // literal string "Integrated Camera (5986:2113)" in useState, so the panel
  // named a camera unrelated to the machine, and choosing one set a variable
  // nothing read.
  const { cams } = useDeviceEnumeration();
  const selectedCamId = useDevicesStore((s) => s.selectedCamId);
  const selectCam = useDevicesStore((s) => s.selectCam);
  const cameraLabel =
    cams.find((d) => d.deviceId === selectedCamId)?.label || t("controlBar.selectCamera");
  // A real capture preset now, not a label. Was useState("Medium") with five
  // menu entries that changed a local variable and nothing else.
  const videoQuality = useDevicesStore((s) => s.videoQuality);
  const setVideoQuality = useDevicesStore((s) => s.setVideoQuality);
  // The grid's page size, shared with the Adjust View dialog rather than a
  // second, disagreeing copy. Was useState("1") — which also mislabelled the
  // default, since the grid has never shown a single tile.
  //
  // This control counts OTHER people, which is what its label promises: pick 2
  // and you get two of them plus your own tile, three tiles to a page. The
  // store counts tiles, because that is what the grid draws and what lastN
  // consumes. `tilesForVisibleVideos` is the whole of the difference, and it
  // lives in the store beside the value so the two dropdowns writing this field
  // cannot drift a tile apart.
  const tileDensity = useMeetingLayoutStore((s) => s.tileDensity);
  const setTileDensity = useMeetingLayoutStore((s) => s.setTileDensity);
  const visibleVideos =
    tileDensity === "auto" ? "Auto" : String(visibleVideosForTiles(tileDensity));
  const setVisibleVideos = (v: string) =>
    setTileDensity(v === "Auto" ? "auto" : tilesForVisibleVideos(Number(v)));
  /**
   * The choices, in other-people terms.
   *
   * Stops at 15 — the same number Auto resolves to, so the list tops out where
   * the default sits rather than offering densities past it. Every extra tile
   * on the page is another camera lastN has to keep flowing and another face
   * shrunk to fit, and the grid stops being watchable well before the counts
   * that used to be on offer here.
   */
  const VISIBLE_VIDEO_CHOICES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 15];
  // Maps onto the LayoutMode the grid already understands. Was
  // useState("Democratic view") against a store that had 'tiled', 'spotlight'
  // and 'sidebar' the whole time.
  const preferredLayout = useMeetingPreferencesStore((s) => s.preferredLayout);
  const setPreferredLayout = useMeetingPreferencesStore((s) => s.setPreferredLayout);
  const setLayoutMode = useMeetingLayoutStore((s) => s.setLayoutMode);
  const LAYOUT_LABELS: Record<PreferredLayout, string> = {
    tiled: "Democratic view",
    spotlight: "Spotlight view",
    sidebar: "Sidebar view",
  };
  const roomLayout = LAYOUT_LABELS[preferredLayout];
  const chooseLayout = (layout: PreferredLayout) => {
    setPreferredLayout(layout);
    // Applied at once as well as remembered: a layout setting that only takes
    // effect after the next screen share would look broken.
    setLayoutMode(layout);
  };
  // Real capture constraints now. Both fold into the quality preset rather
  // than sitting beside it, so resolution / fps / ratio describe one capture
  // instead of three controls fighting over the same getUserMedia call.
  const ASPECT_RATIOS: Record<string, number> = {
    "16 : 9": 16 / 9,
    "4 : 3": 4 / 3,
    "1 : 1": 1,
  };
  const aspectValue = useDevicesStore((s) => s.aspectRatio);
  const setAspectValue = useDevicesStore((s) => s.setAspectRatio);
  const aspectRatio =
    Object.keys(ASPECT_RATIOS).find((k) => ASPECT_RATIOS[k] === aspectValue) ?? "16 : 9";
  const setAspectRatio = (label: string) => setAspectValue(ASPECT_RATIOS[label] ?? 16 / 9);
  const webcamFps = useDevicesStore((s) => s.webcamFrameRate);
  const setWebcamFps = useDevicesStore((s) => s.setWebcamFrameRate);
  // "Preset" rather than a number: the quality preset already carries an fps,
  // and this control overrides it only when the user asks for something else.
  const webcamFrameRate = webcamFps === null ? "Preset" : String(webcamFps);
  const setWebcamFrameRate = (v: string) => setWebcamFps(v === "Preset" ? null : Number(v));
  // Real getDisplayMedia constraint now, applied to the next share.
  const screenShareFrameRate = String(useDevicesStore((s) => s.screenShareFrameRate));
  const setScreenShareFps = useDevicesStore((s) => s.setScreenShareFrameRate);
  const setScreenShareFrameRate = (v: string) => setScreenShareFps(Number(v));
  // The container local recordings are written in. Was useState("video/webm")
  // while the recorder always preferred MP4 and never consulted it.
  const videoMimeType = useMeetingPreferencesStore((s) => s.recordingContainer);
  const setVideoMimeType = useMeetingPreferencesStore((s) => s.setRecordingContainer);

  return (
    <div className="flex-1 max-h-[400px] overflow-x-hidden overflow-y-auto px-2 space-y-5">
      {/* Select Camera */}
      <div className="space-y-2">
        <label className="text-xs text-slate-500 dark:text-muted-foreground uppercase tracking-wider font-medium">
          {t("controlBar.selectCamera")}
        </label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="w-full flex items-center justify-between bg-transparent border-b border-slate-300 dark:border-border py-2  text-sm text-slate-700 dark:text-foreground focus:outline-none focus:border-primary cursor-pointer">
              <span className="truncate pr-4">{cameraLabel}</span>
              <ChevronDown className="h-4 w-4 text-slate-400 dark:text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="min-w-[var(--radix-dropdown-menu-trigger-width)] bg-card border-slate-200 dark:border-border">
            {cams.length === 0 ? (
              <DropdownMenuItem disabled>{t("controlBar.selectCamera")}</DropdownMenuItem>
            ) : (
              cams.map((d) => (
                <DropdownMenuItem
                  key={d.deviceId}
                  onClick={() => selectCam(d.deviceId)}
                  className="truncate"
                >
                  {d.label || t("controlBar.selectCamera")}
                </DropdownMenuItem>
              ))
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Select video quality */}
      <div className="space-y-2">
        <label className="text-xs text-slate-500 dark:text-muted-foreground uppercase tracking-wider font-medium">
          {t("controlBar.selectVideoQuality")}
        </label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="w-full flex items-center justify-between bg-transparent border-b border-slate-300 dark:border-border py-2  text-sm text-slate-700 dark:text-foreground focus:outline-none focus:border-primary cursor-pointer">
              <span>{videoQuality}</span>
              <ChevronDown className="h-4 w-4 text-slate-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="min-w-[var(--radix-dropdown-menu-trigger-width)] bg-card border-slate-200 dark:border-border">
            {(Object.keys(VIDEO_QUALITY_PRESETS) as VideoQuality[]).map((q) => (
              <DropdownMenuItem key={q} onClick={() => setVideoQuality(q)}>
                {q}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Select number of visible videos */}
      <div className="space-y-2">
        <label className="text-xs text-slate-500 dark:text-muted-foreground uppercase tracking-wider font-medium">
          {t("controlBar.selectVisibleVideos")}
        </label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="w-full flex items-center justify-between bg-transparent border-b border-slate-300 dark:border-border py-2  text-sm text-slate-700 dark:text-foreground focus:outline-none focus:border-primary cursor-pointer">
              <span>{visibleVideos}</span>
              <ChevronDown className="h-4 w-4 text-slate-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="min-w-[var(--radix-dropdown-menu-trigger-width)] max-h-60 overflow-y-auto">
            <DropdownMenuItem onClick={() => setVisibleVideos("Auto")}>
              Auto ({AUTO_VISIBLE_VIDEOS})
            </DropdownMenuItem>
            {VISIBLE_VIDEO_CHOICES.map((n) => (
              <DropdownMenuItem key={n} onClick={() => setVisibleVideos(n.toString())}>
                {n}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Mirror view of own video */}
      <div className="flex items-center justify-between py-2">
        <span className="text-sm text-slate-700 dark:text-foreground">{t("controlBar.mirrorView")}</span>
        <Switch checked={mirrorView} onCheckedChange={setMirrorView} />
      </div>

      {/* Advanced video settings - Collapsible */}
      <div className="pt-2">
        <button
          type="button"
          onClick={() => setAdvancedOpen(!advancedOpen)}
          className="flex items-center justify-between w-full py-2 text-sm font-semibold text-slate-800 dark:text-foreground hover:text-slate-600 dark:hover:text-foreground/80 transition-colors"
        >
          <span>{t("controlBar.advancedVideoSettings")}</span>
          {advancedOpen ? (
            <ChevronUp className="h-4 w-4 text-slate-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-400" />
          )}
        </button>

        {advancedOpen && (
          <div className="space-y-5 pt-3">
            {/* Room layout */}
            <div className="space-y-2">
              <label className="text-xs text-slate-500 dark:text-muted-foreground uppercase tracking-wider font-medium">
                {t("controlBar.selectRoomLayout")}
              </label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-full flex items-center justify-between bg-transparent border-b border-slate-300 dark:border-border py-2  text-sm text-slate-700 dark:text-foreground focus:outline-none focus:border-primary cursor-pointer">
                    <span>{roomLayout}</span>
                    <ChevronDown className="h-4 w-4 text-slate-400 dark:text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="min-w-[var(--radix-dropdown-menu-trigger-width)]">
                  {(Object.keys(LAYOUT_LABELS) as PreferredLayout[]).map((m) => (
                    <DropdownMenuItem key={m} onClick={() => chooseLayout(m)}>
                      {LAYOUT_LABELS[m]}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Aspect ratio */}
            <div className="space-y-2">
              <label className="text-xs text-slate-500 dark:text-muted-foreground uppercase tracking-wider font-medium">
                {t("controlBar.selectVideoAspectRatio")}
              </label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-full flex items-center justify-between bg-transparent border-b border-slate-300 dark:border-border py-2  text-sm text-slate-700 dark:text-foreground focus:outline-none focus:border-primary cursor-pointer">
                    <span>{aspectRatio}</span>
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="min-w-[var(--radix-dropdown-menu-trigger-width)]">
                  <DropdownMenuItem onClick={() => setAspectRatio("16 : 9")}>16 : 9</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setAspectRatio("4 : 3")}>4 : 3</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setAspectRatio("1 : 1")}>1 : 1</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Webcam frame rate */}
            <div className="space-y-2">
              <label className="text-xs text-slate-500 dark:text-muted-foreground uppercase tracking-wider font-medium">
                {t("controlBar.selectWebcamFrameRate")}
              </label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-full flex items-center justify-between bg-transparent border-b border-slate-300 dark:border-border py-2  text-sm text-slate-700 dark:text-foreground focus:outline-none focus:border-primary cursor-pointer">
                    <span>{webcamFrameRate}</span>
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="min-w-[var(--radix-dropdown-menu-trigger-width)]">
                  {["Preset", "15", "24", "25", "30", "60"].map((v) => (
                    <DropdownMenuItem key={v} onClick={() => setWebcamFrameRate(v)}>
                      {v}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Screen sharing frame rate */}
            <div className="space-y-2">
              <label className="text-xs text-slate-500 dark:text-muted-foreground uppercase tracking-wider font-medium">
                {t("controlBar.selectScreenShareFrameRate")}
              </label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-full flex items-center justify-between bg-transparent border-b border-slate-300 dark:border-border py-2  text-sm text-slate-700 dark:text-foreground focus:outline-none focus:border-primary cursor-pointer">
                    <span>{screenShareFrameRate}</span>
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="min-w-[var(--radix-dropdown-menu-trigger-width)]">
                  <DropdownMenuItem onClick={() => setScreenShareFrameRate("15")}>15</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setScreenShareFrameRate("30")}>30</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setScreenShareFrameRate("5")}>5</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setScreenShareFrameRate("60")}>60</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Video mime type */}
            <div className="space-y-2">
              <label className="text-xs text-slate-500 dark:text-muted-foreground uppercase tracking-wider font-medium">
                {t("controlBar.selectVideoMimeType")}
              </label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="w-full flex items-center justify-between bg-transparent border-b border-slate-300 dark:border-border py-2  text-sm text-slate-700 dark:text-foreground focus:outline-none focus:border-primary cursor-pointer">
                    <span>{videoMimeType}</span>
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="min-w-[var(--radix-dropdown-menu-trigger-width)]">
                  <DropdownMenuItem onClick={() => setVideoMimeType("video/mp4")}>video/mp4</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setVideoMimeType("video/webm")}>video/webm</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

